// PatientSearchAutocomplete.tsx
import React, { useEffect, useState } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import HttpClient, { HttpGetOutput } from "../../lib/HttpClient";
import { useStyles } from "./styleUtils";

/** Clean UI-facing patient type */
export type PatientSuggestion = {
  id: string;
  name: string;
  age?: string | null;
  gender?: string | null;
  mobile?: string;
  visitDate?: string;
};

type Props = {
  patientName: string;
  onNameChangeHandler: (value: string) => void;
  onSelectHandler: (patient: PatientSuggestion | null) => void;
};

const PatientSearchAutocomplete: React.FC<Props> = ({
  patientName,
  onNameChangeHandler,
  onSelectHandler,
}) => {
  const classes = useStyles();

  const [options, setOptions] = useState<PatientSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  /** API call */
  const searchPatients = async (
    query: string
  ): Promise<PatientSuggestion[]> => {
    if (!query || query.trim() === "") return [];

    const url = `/api/v1/users/search?query=${encodeURIComponent(
      query.trim()
    )}`;

    try {
      const resp = await HttpClient.performGet(url);
      const body = (resp as HttpGetOutput<any>)?.body;
      const profiles = body?.profiles ?? [];

      // 🔽 Normalize backend response
      return profiles.map((p: any) => ({
        id: p.appId,
        name: p.name,
        age: p.age ?? null,
        gender: p.gender ?? null,
        mobile: p.mobile,
        visitDate: p.visitDate,
      }));
    } catch (err) {
      console.error("Patient search failed:", err);
      return [];
    }
  };

  /** Fetch when search text changes */
  useEffect(() => {
    let mounted = true;

    if (!patientName || patientName.trim() === "") {
      setOptions([]);
      return;
    }

    setLoading(true);

    (async () => {
      const results = await searchPatients(patientName);
      if (mounted) {
        setOptions(results);
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [patientName]);

  return (
    <Autocomplete
      id="patient-search-autocomplete"
      options={options}
      open={open}
      loading={loading}
      filterOptions={(x) => x} // server-side filtering
      getOptionLabel={(option: PatientSuggestion) =>
        `${option.name} - ${option.id}`
      }
      onChange={(_, value) => {
        onSelectHandler(value);
        setOpen(false);
      }}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      renderOption={(option) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {option.name} – {option.id}
          {(option.age || option.gender) && (
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              {option.gender ?? "—"} • {option.age ?? "—"}
            </span>
          )}
        </div>
      )}
      renderInput={(params) => (
        <div
          ref={params.InputProps.ref}
          style={{ position: "relative", width: "100%" }}
        >
          <input
            {...params.inputProps}
            className={classes.textField}
            placeholder="Search patient by name"
            value={patientName}
            onClick={() => setOpen(true)}
            onChange={(e) => {
              setOpen(true);
              onNameChangeHandler(e.target.value);
            }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "8px 10px",
              borderRadius: 4,
              border: "1px solid #ccc",
            }}
          />
          {loading && (
            <span
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 12,
                opacity: 0.7,
              }}
            >
              Searching…
            </span>
          )}
        </div>
      )}
    />
  );
};

export default PatientSearchAutocomplete;
